import { SHFilterOption } from "@components/design-systems";
import {
  AnalysisFeePortfolioDTO,
  AnalysisFeePortfolioSetupDTO,
  FeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useFeeCalculator } from "./useFeeCalculator";

export const useFeeCalculatorPortfolioSetup = () => {
  const useFeeCalculatorReturnValues = useFeeCalculator();
  const {
    formMethods: { control, setValue: setFormValue, getValues: getFormValues },
    calculatedTotals,
    investmentTypeSelections,
  } = useFeeCalculatorReturnValues;

  const watchedPortfolioSetups = useWatch({
    control,
    name: "portfolioSetups",
  });

  const watchedPortfolios: AnalysisFeePortfolioDTO[] | undefined = useWatch({
    control,
    name: "portfolios",
  });

  const watchedFeeCalculators: FeeCalculatorDTO[] | undefined = useWatch({
    control,
    name: "feeCalculators",
  });

  const updatePortfolios = (portfolios: AnalysisFeePortfolioDTO[]) => {
    setFormValue("portfolios", portfolios, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updatePortfolioSetups = (
    portfolioSetups: AnalysisFeePortfolioSetupDTO[],
    shouldDirty?: boolean,
  ) => {
    setFormValue("portfolioSetups", portfolioSetups, {
      shouldDirty: shouldDirty ?? true,
      shouldValidate: true,
    });
  };

  // #region Return values
  const portfolioSetups = useMemo(() => {
    return watchedPortfolioSetups || [];
  }, [watchedPortfolioSetups]);

  const portfolios: AnalysisFeePortfolioDTO[] = useMemo(() => {
    return watchedPortfolios || [];
  }, [watchedPortfolios]);

  const portfolioSelections: SHFilterOption[] = useMemo(
    () =>
      watchedPortfolios?.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [watchedPortfolios],
  );

  const feeCalculators = useMemo(() => {
    return watchedFeeCalculators || [];
  }, [watchedFeeCalculators]);
  // #endregion Return values

  return {
    control,
    getFormValues,
    setFormValue,
    portfolioSetups,
    feeCalculators,
    portfolios,
    portfolioSelections,
    calculatedTotals,
    investmentTypeSelections,
    useFeeCalculatorReturnValues,
    updatePortfolios,
    updatePortfolioSetups,
  };
};
